// @ts-strict-ignore
import React, {
  useCallback, useContext,
  useEffect, useMemo, useRef, useState,
} from 'react';

import { selectExpressionActiveSubEditor, selectFocusedAction, selectWorkflowData } from 'flow/store/redux/selectors';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';

import AttributeExpressionGroup from '../../expressions/AttributeExpressionGroup/AttributeExpressionGroup';
import { RuleEditorContext } from '../context';
import { useCaretControl } from '../../useCaretControl/useCaretControl';
import type {
  IsolatedEditingBlockData,
  RuleItemRef,
  RuleItemRefs,
} from '../types';
import type { Action } from '~/dashboard/views/workflows/store/engine/types';
import { MLAEditorContext } from '../../mla/context';
import type { MLASourceData } from '../../mla/types';
import MLAFootnotesContainer from '../../mla/MLAFootnotesContainer/MLAFootnotesContainer';
import {
  settleActionOutputExpressionFunctions,
  resetNodeAndConnectorState,
  setExpressionActiveSubEditor,
} from '~/dashboard/views/workflows/store/redux/slice';
import { getEnclosingFunctionGroupPath } from '../../expressions/AttributeExpressionGroup/helpers';
import { useKeyboardKeyEvent } from 'flow/editor/hooks/useKeyboardKeyEvent';
import { getMLAsUsedInExpression } from '~/dashboard/views/workflows/service/mla';
import { extractDataRouteIds } from '~/dashboard/views/workflows/service/dataRoutes/dataRoutes';
import { CaretIndexType, startingCaretPosition } from '../../useCaretControl/constants';
import type { NodeTooltipProps } from '../../Graph/nodes/BaseNode/NodeTooltip/NodeTooltip';
import { dismissShortcut } from 'flow/editor/hooks/useKeyboardKeyEvent/constants';
import { UserContext } from '~/dashboard/context';
import { createWorkflowHistoryThunk } from 'flow/store/redux/thunks/history';
import { Operation } from 'flow/service/changelog/types';
import { changeDescription } from 'flow/store/redux/types';
import { createUiEffectContext } from 'flow/service/history';

type OutputAttributeTabProps = {
  action?: Action;
  editable?: boolean;
  ActionHoverTooltip?: (NodeTooltipProps: NodeTooltipProps) => JSX.Element;
};

const DROPDOWN_REF_WIDTH_PADDING = 10;

export default function OutputAttributeExpressionTab({
  action,
  editable = true,
  ActionHoverTooltip,
}: OutputAttributeTabProps) {
  const dispatch = useAppDispatch();
  const { featureFlags } = useContext(UserContext);
  const { dataRoutes } = useAppSelector(selectWorkflowData);
  const focusedAction = useAppSelector(selectFocusedAction);
  const sourceAction = useMemo(() => (
    action || focusedAction
  ), [action, focusedAction]);

  const ruleEditorActionPublicId = useRef(null);
  const ruleEditorPanelBoxContainerRef = useRef(null);
  const [
    ruleEditorPanelBoxContainerWidth,
    setRuleEditorPanelBoxContainerWidth,
  ] = useState<number>(0);

  const [
    ruleItemRefs,
    setRuleItemRefs,
  ] = useState<RuleItemRefs>({});

  const updateRuleItemRefs = useCallback((refItem: RuleItemRef) => {
    setRuleItemRefs(prevState => ({
      ...prevState,
      [refItem.path]: {
        width: refItem.width,
      },
    }));
  }, []);

  const isolatedEditingBlock = useAppSelector(selectExpressionActiveSubEditor);
  const setIsolatedEditingBlock = useCallback((editingBlock: IsolatedEditingBlockData) => {
    dispatch(setExpressionActiveSubEditor(editingBlock));
  }, [dispatch]);

  // kept in a ref because the handler needs the caret position this hook returns
  const settleFunctionGroupRef = useRef<(closeEveryGroup?: boolean) => void>(() => {});

  const caretControl = useCaretControl({
    expressionData: sourceAction?.output?.expression,
    sourceId: sourceAction?.publicId,
    caretType: CaretIndexType.EXPRESSION,
    ruleEditorPanelBoxContainerRef,
    mlaEditorEnabled: Boolean(isolatedEditingBlock.originType),
  });

  const semanticLineBreakData = useMemo(() => {
    // expression item refs are all properly generated because it matches with flat caret indices length
    const groupedByLines = [{
      width: 0,
      items: [],
      id: 0,
      mlaEditorInThisLine: false,
    }];
    caretControl.ruleEditorFlatCaretIndices.forEach((ref, index) => {
      const matchingRuleItemRef = ruleItemRefs[ref];
      const itemRefWidth = matchingRuleItemRef
        ? matchingRuleItemRef.width
        : 0;
      const itemRefPath = ref;

      const isMLAEditorEnabled = isolatedEditingBlock.originCaretIndex?.length > 0
        && isolatedEditingBlock.originCaretIndex === itemRefPath;
      if (groupedByLines.at(-1).width + itemRefWidth + DROPDOWN_REF_WIDTH_PADDING
        >= ruleEditorPanelBoxContainerWidth) {
        const ruleItemRefArray = Object.entries(ruleItemRefs);
        if (itemRefPath.endsWith('-1!dataRoute') && ruleItemRefArray.length > 0) {
          // if the break point path ends with -1!dataRoute, we artificially pull the one with -0!dataRoute from the groupByLines[last].items
          // and add/push it to the new line with this current scope's `ref`.
          // because from this component/Expression data representation perspective, there is no -1!dataRoute.
          // only the caret/dropdown indices have -1!dataRoute to be able to support cursor caret position for svc attribute.
          groupedByLines.at(-1).items.pop();
          const prevItem = caretControl.ruleEditorFlatCaretIndices[index - 1];
          const matchingPrevRuleItemRef = ruleItemRefs[prevItem];
          groupedByLines.push({
            width: matchingPrevRuleItemRef.width + itemRefWidth + DROPDOWN_REF_WIDTH_PADDING,
            items: [prevItem, itemRefPath],
            mlaEditorInThisLine: isMLAEditorEnabled,
            id: groupedByLines.length + 1,
          });
        } else {
          groupedByLines.push({
            width: itemRefWidth + DROPDOWN_REF_WIDTH_PADDING,
            items: [itemRefPath],
            mlaEditorInThisLine: isMLAEditorEnabled,
            id: groupedByLines.length + 1,
          });
        }
      } else {
        groupedByLines.at(-1).items.push(itemRefPath);
        if (isMLAEditorEnabled) {
          groupedByLines.at(-1).mlaEditorInThisLine = true;
        }
        groupedByLines.at(-1).width += itemRefWidth;
      }
    });

    return {
      groupedByLines,
      lineBreakPointIndices: groupedByLines.map(group => (group.items.at(-1))),
    };
  }, [caretControl, isolatedEditingBlock, ruleItemRefs, ruleEditorPanelBoxContainerWidth]);

  const mlaUsedInExpressions = useMemo(() => getMLAsUsedInExpression({
    dataRoutes,
    sourceAction,
  }), [sourceAction, dataRoutes]);

  const context = React.useMemo(() => ({
    ruleEditorPanelBoxContainerWidth,
    ruleItemRefs,
    updateRuleItemRefs,
    semanticLineBreakData,
    sourceAction,
    mlaUsedInRules: mlaUsedInExpressions,
    isDragItemHoveringOnDropZone: undefined,
    setIsDragItemHoveringOnDropZone: undefined,
    isolatedEditingBlock,
    setIsolatedEditingBlock,
    ...caretControl,
    ActionHoverTooltip,
  }), [
    ruleEditorPanelBoxContainerWidth,
    ruleItemRefs,
    updateRuleItemRefs,
    semanticLineBreakData,
    sourceAction,
    caretControl,
    mlaUsedInExpressions,
    isolatedEditingBlock,
    setIsolatedEditingBlock,
    ActionHoverTooltip,
  ]);

  useEffect(() => {
    const getContainerWidth = () => {
      const elementStyles = window.getComputedStyle(ruleEditorPanelBoxContainerRef?.current, null);
      const widthNumber = elementStyles.getPropertyValue('width').replace(/\D/g, '');
      setRuleEditorPanelBoxContainerWidth(Number(widthNumber));
    };

    getContainerWidth();
    window.addEventListener('resize', getContainerWidth);
    return () => window.removeEventListener('resize', getContainerWidth);
  }, []);

  const mlaEditorPanelBoxContainerRef = useRef(null);
  const [mlaContextSourceData, setMlaContextSourceData] = useState<MLASourceData>({
    type: undefined,
    data: undefined,
    attributeTokenMap: undefined,
  });

  const mlaCaretControl = useCaretControl({
    mlaData: mlaContextSourceData?.attributeTokenMap?.components,
    caretType: CaretIndexType.MLA,
    sourceId: mlaContextSourceData?.attributeTokenMap?.attribute.attribute_id,
    ruleEditorPanelBoxContainerRef: mlaEditorPanelBoxContainerRef,
  });

  const mlaContext = useMemo(() => ({
    sourceData: mlaContextSourceData,
    setSourceData: setMlaContextSourceData,
    mlaEditorPanelBoxContainerRef,
    handleCloseMLAEditor: () => {},
    ...mlaCaretControl,
  }), [mlaContextSourceData, mlaCaretControl]);

  useEffect(() => {
    // a removed action has no sub editor to reset, and the lines below assume it is there
    if (!sourceAction) return;

    if (ruleEditorActionPublicId.current !== sourceAction.publicId) {
      ruleEditorActionPublicId.current = sourceAction.publicId;

      // Avoid clobbering the sub editor when undo/redo needs to toggle actions but still needs the sub editor to be open
      const activeBlockBelongsToSourceAction = extractDataRouteIds(sourceAction.output.expression)
        .includes(isolatedEditingBlock.originPublicId);

      if (!activeBlockBelongsToSourceAction) {
        setIsolatedEditingBlock({
          originPublicId: undefined,
          originType: undefined,
          originCaretIndex: undefined,
        });
      }
    }
  }, [isolatedEditingBlock.originPublicId, setIsolatedEditingBlock, sourceAction]);

  /*
    A brand new output attribute has no caret anywhere, so there is nowhere to type until it is
    clicked. Placed only once, so it never takes the caret away from the user afterwards.
  */
  useEffect(() => {
    const isEmpty = !sourceAction?.output?.expression?.length;

    if (isEmpty && !caretControl.morphingCaretPosition) {
      caretControl.setMorphingCaretPosition(startingCaretPosition);
    }
  }, [
    sourceAction?.output?.expression?.length,
    caretControl.morphingCaretPosition,
    caretControl.setMorphingCaretPosition,
  ]);

  const settleFunctionGroups = useCallback((closeEveryGroup = false) => {
    if (!featureFlags?.minMaxOutputAttributes) return;

    // node tooltip and the dependents tab render this editor read-only.
    if (!editable) return;

    // when the editor unmounts, the sourceAction could have been removed by node removal
    if (!sourceAction?.publicId || !sourceAction.output?.expression?.length) return;

    // the group the caret is in keeps its comma, so another operand can still be added to it
    const groupToKeepOpen = closeEveryGroup ? undefined : getEnclosingFunctionGroupPath({
      expressions: sourceAction?.output?.expression,
      position: caretControl.morphingCaretPosition,
    });

    /*
      Recorded in history, because removing the trailing operator changes the expression. If it were
      not recorded, a later undo would patch a state history never saw, and the comma would be lost.
      Closing a group that was already closed changes nothing, so the thunk writes no entry.
    */
    dispatch(createWorkflowHistoryThunk(
      () => dispatch(settleActionOutputExpressionFunctions({
        id: sourceAction.publicId,
        keepOpenPath: groupToKeepOpen,
      })),
      Boolean(featureFlags?.workflowEditorUndoRedo),
      {
        operation: Operation.Modify,
        uiEffectContext: createUiEffectContext(changeDescription.expression, {
          actionId: sourceAction.publicId,
          // no path matches an element, so redoing a settle leaves the caret where the step put it
          // rather than throwing it onto the parenthesis of whichever group stayed open
          expressionPath: '',
        }),
      },
    ));
  }, [
    caretControl.morphingCaretPosition, dispatch, sourceAction, editable,
    featureFlags?.workflowEditorUndoRedo, featureFlags?.minMaxOutputAttributes,
  ]);

  useEffect(() => {
    settleFunctionGroupRef.current = settleFunctionGroups;
  }, [settleFunctionGroups]);

  // closing the drawer moves no caret, so nothing else would close the function the caret is in
  useEffect(() => () => settleFunctionGroupRef.current(true), []);

  // any caret move can leave a function, including a click straight into another position
  useEffect(() => {
    settleFunctionGroupRef.current();
  }, [caretControl.morphingCaretPosition]);

  const onEscapeKeyDown = useCallback(() => {
    settleFunctionGroups();

    if (isolatedEditingBlock.originType) {
      setIsolatedEditingBlock({
        originPublicId: undefined,
        originType: undefined,
        originCaretIndex: undefined,
      });
    } else {
      dispatch(resetNodeAndConnectorState());
    }
  }, [dispatch, isolatedEditingBlock, settleFunctionGroups]);

  useKeyboardKeyEvent({
    shortcut: dismissShortcut,
    callback: onEscapeKeyDown,
  });

  // The editor renders once more after its action is removed, with no expression left to draw.
  // Kept below every hook so this early return cannot change the order they run in.
  if (!sourceAction) return null;

  return (
    <RuleEditorContext.Provider value={context}>
      <MLAEditorContext.Provider value={mlaContext}>
        <div
          ref={ruleEditorPanelBoxContainerRef}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            rowGap: '3px',
            padding: editable ? 'var(--cl-space-4)' : 'none',
            boxSizing: 'content-box',
          }}
        >
          <AttributeExpressionGroup
            editable={editable}
            expressions={sourceAction.output.expression}
            expressionPath=""
          />
        </div>
        {editable && <MLAFootnotesContainer />}
      </MLAEditorContext.Provider>
    </RuleEditorContext.Provider>
  );
}
